import React from 'react';
import { Leaf, Heart, Users, Globe } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FDF6E3] pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#3D2314] to-[#5C3D2E] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#D4A853] font-medium text-sm tracking-widest uppercase">Our Story</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDF6E3] mt-4 mb-6">
            The House of Bania Legacy
          </h1>
          <p className="text-[#FDF6E3]/70 max-w-2xl mx-auto text-lg">
            A journey from the kitchens of Marwar to the world
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C17817] font-medium text-sm tracking-widest uppercase">Our Beginning</span>
              <h2 className="font-serif text-4xl font-bold text-[#5C3D2E] mt-3 mb-6">
                Rooted in Tradition, Crafted with Love
              </h2>
              <div className="space-y-4 text-[#5C3D2E]/70 leading-relaxed">
                <p>
                  House of Bania was born from a simple longing — the taste of home. As families from Marwar 
                  spread across the globe, they carried with them memories of grandmother's pickles, 
                  mother's mustard oil fragrance, and the crunch of fresh makhana.
                </p>
                <p>
                  We started with a mission to bring these authentic flavors to every Indian household 
                  abroad. No shortcuts, no preservatives, no compromises — just pure, heritage recipes 
                  passed down through generations.
                </p>
                <p>
                  Today, we proudly serve families in Singapore, Dubai, Australia, and the United Kingdom, 
                  bridging the gap between tradition and taste, one product at a time.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=800&auto=format&fit=crop"
                  alt="Traditional Indian Spices"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-[#C17817]/10 rounded-2xl -z-10" />
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#D4A853]/20 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C17817] font-medium text-sm tracking-widest uppercase">Our Values</span>
            <h2 className="font-serif text-4xl font-bold text-[#5C3D2E] mt-3">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Leaf,
                title: 'Pure & Natural',
                description: 'No preservatives, no chemicals. Just pure, natural ingredients from trusted sources.',
              },
              {
                icon: Heart,
                title: 'Made with Love',
                description: 'Every product is handcrafted in small batches with the same care as home cooking.',
              },
              {
                icon: Users,
                title: 'Family Recipes',
                description: 'Heirloom recipes passed through generations of Marwari families.',
              },
              {
                icon: Globe,
                title: 'Global Reach',
                description: 'Bringing the taste of India to NRI families across the world.',
              },
            ].map((value, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-[#FDF6E3] border border-[#D4A853]/20 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-[#C17817] to-[#D4A853] flex items-center justify-center mb-6">
                  <value.icon className="h-8 w-8 text-[#FDF6E3]" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#5C3D2E] mb-3">{value.title}</h3>
                <p className="text-[#5C3D2E]/60 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Section */}
      <section className="py-24 bg-[#3D2314]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4A853] font-medium text-sm tracking-widest uppercase">Our Process</span>
            <h2 className="font-serif text-4xl font-bold text-[#FDF6E3] mt-3">
              Proudly Made in India
            </h2>
            <p className="text-[#FDF6E3]/60 mt-4 max-w-2xl mx-auto">
              We source, craft, and package all our products in India, ensuring authenticity at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Source',
                description: 'Ingredients sourced directly from farmers and trusted suppliers across India.',
              },
              {
                step: '02',
                title: 'Craft',
                description: 'Handmade in small batches using traditional methods and family recipes.',
              },
              {
                step: '03',
                title: 'Deliver',
                description: 'Carefully packaged and shipped to your doorstep, maintaining freshness.',
              },
            ].map((item, index) => (
              <div key={index} className="relative p-8 rounded-2xl bg-[#FDF6E3]/5 border border-[#D4A853]/20">
                <span className="text-6xl font-bold text-[#D4A853]/20 absolute top-4 right-4">
                  {item.step}
                </span>
                <h3 className="font-serif text-2xl font-bold text-[#FDF6E3] mb-4">{item.title}</h3>
                <p className="text-[#FDF6E3]/60">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
